import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { GlobalErrorHandlerService } from '@k12/errors';
import { ActivatedRoute } from '@angular/router';
import { TasksService, TaskTypeService } from '@k12/tasks';
import { ToastMessageService, LoadingSpinnerService } from '@k12/app-services';
import { catchError, EMPTY, finalize, map, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ToastMessageType, SubTask, Task, TaskType, TaskTypeForms, ProgramType } from '@k12/models';
import { HomeSchoolRegistrationInformation } from './forms/home-school-registration-information';
import { HouseholdTransferWithdrawalFormComponent } from './forms/household-transfer-withdrawal-form/household-transfer-withdrawal-form.component';
import { SchoolTransferAndWithdrawalFormComponent } from './forms/school-transfer-and-withdrawal-form/school-transfer-and-withdrawal-form.component';
import { EnrollmentVerificationFormComponent } from './forms/enrollment-verification-form.component';
import { AwardEndorsementFormComponent } from './forms/award-endorsement-form.component';
import { AwardAcceptanceFormComponent } from './forms/award-acceptance-form.component';
import { LeaReleaseFormComponent } from './forms/lea-release-form.component';
import { TaskHeaderInfoComponent } from '@k12/task-view';
import { IncomeVerificationFormComponent } from './forms/income-verification-form/income-verification-form.component';
import { VerificationOfNonFilingFormComponent } from './forms/verification-of-non-filing-form/verification-of-non-filing-form.component';
import { GuardianChangeFormComponent } from './forms/guardian-change-form/guardian-change-form.component';
import {
  SchoolCostCorrectionFormComponent
} from './forms/school-cost-correction-form/school-cost-correction-form.component';
import { AwardEndorsementTaskFormComponent } from './forms/award-endorsement/award-endorsement.component';
type TaskContext = { taskId: string; taskActionId: string; task: Task; taskAction: SubTask };
type TaskTypeContext = TaskContext & { taskType: TaskType; taskTypeAction: TaskTypeForms };

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-task-form',
  standalone: true,
  imports: [
    HomeSchoolRegistrationInformation,
    SchoolTransferAndWithdrawalFormComponent,
    HouseholdTransferWithdrawalFormComponent,
    EnrollmentVerificationFormComponent,
    AwardEndorsementFormComponent,
    AwardAcceptanceFormComponent,
    LeaReleaseFormComponent,
    TaskHeaderInfoComponent,
    IncomeVerificationFormComponent,
    VerificationOfNonFilingFormComponent,
    GuardianChangeFormComponent,
    SchoolCostCorrectionFormComponent,
    AwardEndorsementTaskFormComponent,
  ],
  templateUrl: './task-form.component.html',
  styleUrl: './task-form.component.scss'
})
export class TaskFormComponent implements OnInit, OnDestroy {
  private readonly globalErrorHandlerService: GlobalErrorHandlerService = inject(GlobalErrorHandlerService);

  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly taskService: TasksService = inject(TasksService);
  private readonly taskTypeService: TaskTypeService = inject(TaskTypeService);
  private readonly toastService = inject(ToastMessageService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly unsubscribe = new Subject<void>();

  protected task: Task | null = null;
  protected taskType: TaskType;
  protected taskAction: SubTask | null = null;
  protected taskTypeAction: TaskTypeForms | null = null;
  protected recentTaskNote: string = '';
  protected ProgramType = ProgramType;

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public ngOnInit(): void {
    this.route.params
      .pipe(
        takeUntil(this.unsubscribe),
        map((params) => ({
          taskId: params['id'] as string | undefined,
          taskActionId: params['typeId'] as string | undefined
        })),
        switchMap(({ taskId, taskActionId }) => {
          if (!taskId || !taskActionId) {
            this.showError('Invalid task or task action ID.');
            return EMPTY;
          }

          this.loadingSpinnerService.showSpinner();

          return this.loadTaskContext(taskId, taskActionId).pipe(
            switchMap((ctx) => this.loadTaskTypeContext(ctx)),
            switchMap((ctx) => {
              if (ctx.taskTypeAction && ctx.taskTypeAction.$type?.toLowerCase() !== 'form') {
                this.showError('This is not a link task template');
                return EMPTY;
              }
              return this.loadRecentTaskNote(ctx.taskId);
            }),
            finalize(() => {
              this.loadingSpinnerService.hideSpinner();
              this.cdr.markForCheck();
            })
          );
        })
      )
      .subscribe();
  }

  private loadTaskContext(taskId: string, taskActionId: string) {
    return this.taskService.get(taskId).pipe(
      map((resp) => resp.body),
      switchMap((task) => {
        if (!task?.subTasks) {
          this.showError('Task has no subtasks.');
          return EMPTY;
        }

        const taskAction = task.subTasks.find((subTask) => subTask.id?.toLowerCase() === taskActionId.toLowerCase());

        if (!taskAction) {
          this.showError('Task action not found.');
          return EMPTY;
        }

        this.task = task;
        this.taskAction = taskAction;

        return of({ taskId, taskActionId, task, taskAction });
      }),
      catchError((error) => {
        this.toastService.showToast(ToastMessageType.Error, 'Loading Task details failed.');
        this.globalErrorHandlerService.handleError(error);
        return EMPTY;
      })
    );
  }

  private loadTaskTypeContext(ctx: TaskContext) {
    return this.taskTypeService.get(ctx.taskAction.taskTypeId).pipe(
      map((resp) => resp.body),
      switchMap((taskType) => {
        if (!taskType) {
          this.toastService.showToast(ToastMessageType.Error, 'Task type not found.');
          this.globalErrorHandlerService.handleError({ message: 'Task type not found.' });
          return EMPTY;
        }
        const subTaskId = ctx.taskAction.taskTypeSubTaskId?.toLowerCase();
        const taskTypeAction = subTaskId
          ? (taskType.subTasks?.find((subTask) => subTask.actionId?.toLowerCase() === subTaskId) as
              | TaskTypeForms
              | undefined)
          : undefined;

        this.taskType = taskType;
        this.taskTypeAction = taskTypeAction ?? null;

        return of({ ...ctx, taskType, taskTypeAction } as TaskTypeContext);
      }),
      catchError((error) => {
        this.toastService.showToast(ToastMessageType.Error, 'Task link failed.');
        this.globalErrorHandlerService.handleError(error);
        return EMPTY;
      })
    );
  }

  private loadRecentTaskNote(taskId: string) {
    return this.taskService.getTaskNotes(taskId).pipe(
      tap((resp) => {
        const apiNotes = resp?.body ?? [];
        this.recentTaskNote = apiNotes.find((note) => note.taskNoteType === 1)?.comments || '';
      }),
      map(() => undefined),
      catchError((err) => {
        console.error('Failed to load task notes', err);
        return of(undefined);
      })
    );
  }

  private showError(message: string): void {
    this.toastService.showToast(ToastMessageType.Error, message);
    this.globalErrorHandlerService.handleError({ message });
  }

  protected updateTask(formData: string) {
    // find the subtask being updated in the collection on task
    if (this.task?.subTasks && this.taskAction) {
      const subtaskToUpdate = this.task.subTasks.find((subtask) => subtask.id === this.taskAction?.id);
      if (!subtaskToUpdate) {
        return;
      }
      // set the formdata and update the task status to in review
      subtaskToUpdate.taskStatusId = 2;
      subtaskToUpdate.formData = formData;
      this.loadingSpinnerService.showSpinner();
      this.taskService
        .updateTask(this.task)
        .then(() => {
          this.loadingSpinnerService.hideSpinner();
          this.toastService.showToast(ToastMessageType.Success, 'Your Response has been uploaded successfully.');
          this.onBack();
        })
        .catch(() => {
          this.loadingSpinnerService.hideSpinner();
          this.toastService.showToast(ToastMessageType.Error, 'Error updating form response.');
        });
    }
  }

  private onBack(): void {
    globalThis.history.back();
  }
}
